import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { API, ADMIN_TOKEN } from "../constants";
import { useGallery } from "../hooks/useGallery";
import { useTutorial } from "../hooks/useTutorial";
import { UploadIcon, TrashIcon, CloseIcon } from "../icons";
import ConfirmDialog from "../components/ConfirmDialog";
import TutorialOverlay from "../components/TutorialOverlay";
import "./GalleryScreen.css";

// ── Progressive image: shows thumbnail instantly, swaps to full-res silently ─
function ProgressiveImage({ thumb, full, alt, className }) {
  const [src, setSrc]       = useState(thumb || full);
  const [sharp, setSharp]   = useState(!thumb || thumb === full);

  useEffect(() => {
    setSrc(thumb || full);
    setSharp(!thumb || thumb === full);
    if (!thumb || !full || thumb === full) return;
    const img = new Image();
    img.onload  = () => { setSrc(full); setSharp(true); };
    img.onerror = () => setSharp(true); // still show thumb on error
    img.src = full;
    return () => { img.onload = null; img.onerror = null; };
  }, [thumb, full]);

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={{
        filter:     sharp ? "none" : "blur(6px)",
        transition: "filter 0.4s ease",
      }}
    />
  );
}

const COLS_KEY    = "wedding_gallery_cols";
const ADMIN_TAPS  = 5;

const TUTORIAL_STEPS = [
  { icon: "📷", text: "Naciśnij <strong>Dodaj zdjęcia / filmy</strong>, aby wgrać swoje wspomnienia." },
  { icon: "🔍", text: "Kliknij dowolne zdjęcie lub film, aby zobaczyć je na pełnym ekranie i dodać komentarz." },
  { icon: "👤", text: "Użyj <strong>filtru</strong>, aby zobaczyć zdjęcia od konkretnych osób." },
  { icon: "📐", text: "Wybierz układ <strong>1–4 kolumn</strong> za pomocą ikon." },
];

function readCols() { return parseInt(localStorage.getItem(COLS_KEY) || "2", 10); }

const COL_OPTIONS = [1, 2, 3, 4];
const ColIcon = ({ n }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" style={{ width: 16, height: 16 }}>
    {Array.from({ length: n }).map((_, i) => (
      <rect key={i} x={1 + i * (18 / n)} y="1" width={18 / n - 1} height="18" rx="1.5" />
    ))}
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
    <circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.5)" />
    <polygon points="10,8 18,12 10,16" fill="white" />
  </svg>
);

export default function GalleryScreen({ userId, userName, adminMode, onAdminChange }) {
  const { photos, uploading, uploadProgress, uploadMultiple, deletePhoto,
          hasMore, loadingMore, loadMore, addComment }
    = useGallery(userId, userName);
  const tutorial = useTutorial("gallery");

  const [lightbox, setLightbox]           = useState(null);
  const [adminInput, setAdminInput]       = useState("");
  const [showAdminInput, setShowAdminInput] = useState(false);
  const [cols, setCols]                   = useState(readCols);
  const [confirm, setConfirm]             = useState(null);
  const [deleteError, setDeleteError]     = useState(null);
  const [tapCount, setTapCount]           = useState(0);
  const [filterNames, setFilterNames]     = useState([]);
  const [showFilter, setShowFilter]       = useState(false);
  const [commentText, setCommentText]           = useState("");
  const [allUploaderNames, setAllUploaderNames] = useState([]);
  const [filteredPhotos, setFilteredPhotos]     = useState(null); // null = no filter active
  const [filterLoading, setFilterLoading]       = useState(false);
  const tapTimer    = useRef(null);
  const fileRef     = useRef();
  const sentinelRef = useRef(null);

  // Load ALL uploader names on mount (not just from the current page)
  useEffect(() => {
    fetch(`${API}/api/gallery/uploaders`)
      .then((r) => (r.ok ? r.json() : []))
      .then((names) => setAllUploaderNames(names))
      .catch(() => {});
  }, []);

  // When filter changes → fetch matching photos from server (bypasses pagination)
  useEffect(() => {
    if (!filterNames.length) {
      setFilteredPhotos(null);
      return;
    }
    setFilterLoading(true);
    const qs = filterNames
      .map((n) => `uploaderName=${encodeURIComponent(n)}`)
      .join("&");
    fetch(`${API}/api/gallery?${qs}&limit=1000`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => setFilteredPhotos(data.items || []))
      .catch(() => setFilteredPhotos([]))
      .finally(() => setFilterLoading(false));
  // filterNames is compared by value via .join below — safe to list as dep
  // eslint-disable-next-line
  }, [filterNames.join(",")]);

  // Infinite scroll — watch sentinel div
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: "200px" }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [loadMore]);

  // Close filter panel on outside click
  useEffect(() => {
    if (!showFilter) return;
    const handler = (e) => {
      if (!e.target.closest(".gallery__filter-wrap")) setShowFilter(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showFilter]);

  // Easter egg: 5 taps on title → admin input
  const handleTitleTap = useCallback(() => {
    setTapCount((prev) => {
      const next = prev + 1;
      clearTimeout(tapTimer.current);
      if (next >= ADMIN_TAPS) { setShowAdminInput(true); return 0; }
      tapTimer.current = setTimeout(() => setTapCount(0), 2000);
      return next;
    });
  }, []);

  const tryAdmin = () => {
    if (adminInput === ADMIN_TOKEN) { onAdminChange(true); setShowAdminInput(false); setAdminInput(""); }
    else { setAdminInput(""); setShowAdminInput(false); alert("Nieprawidłowe hasło"); }
  };
  const logout = () => { onAdminChange(false); setShowAdminInput(false); };

  const changeCols = (n) => { setCols(n); localStorage.setItem(COLS_KEY, String(n)); };

  const requestDelete = (photo) =>
    setConfirm({ photo, message: `Usunąć ten plik od „${photo.uploaderName}"?` });

  const executeDelete = async () => {
    const { photo } = confirm;
    setConfirm(null);
    const result = await deletePhoto(photo, adminMode);
    if (result?.error) setDeleteError(result.error);
    else if (lightbox?.id === photo.id) setLightbox(null);
  };

  const canDelete = (photo) => photo.uploaderId === userId || adminMode;

  // All uploader names: from API (full list) merged with names from loaded photos
  const availableNames = useMemo(() => {
    const fromLoaded = photos.map((p) => p.uploaderName).filter(Boolean);
    return [...new Set([...allUploaderNames, ...fromLoaded])].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [allUploaderNames, photos]);

  const toggleFilter = (name) =>
    setFilterNames((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );

  // When filter active: use server-fetched results (all pages); else normal paginated list
  const displayPhotos = filteredPhotos !== null ? filteredPhotos : photos;

  // Submit comment
  const submitComment = async () => {
    if (!commentText.trim() || !lightbox) return;
    const saved = await addComment(lightbox.id, commentText.trim());
    if (saved) {
      setLightbox((prev) => ({ ...prev, comments: [...(prev.comments || []), saved] }));
      setCommentText("");
    }
  };

  return (
    <div className="screen">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-header__title" onClick={handleTitleTap}>Galeria</h1>
        <p className="page-header__sub">Wspólne wspomnienia</p>
        <button className="help-btn" onClick={tutorial.reopen} title="Pomoc">?</button>
        {adminMode && (
          <div className="gallery__admin-active">
            <span className="gallery__admin-badge">🔐 Tryb admina</span>
            <button className="gallery__admin-logout" onClick={logout}>Wyloguj</button>
          </div>
        )}
        {!adminMode && showAdminInput && (
          <div className="gallery__admin-row">
            <input className="gallery__admin-input" type="password" placeholder="Hasło admina"
              value={adminInput} onChange={(e) => setAdminInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") tryAdmin(); if (e.key === "Escape") setShowAdminInput(false); }}
              autoFocus />
            <button className="gallery__admin-ok" onClick={tryAdmin}>OK</button>
            <button className="gallery__admin-cancel" onClick={() => setShowAdminInput(false)}>✕</button>
          </div>
        )}
      </div>

      {/* Scrollable body */}
      <div className="screen__body">

        {/* Upload */}
        <div className="gallery__upload-section">
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple style={{ display: "none" }}
            onChange={(e) => {
              const files = Array.from(e.target.files);
              if (files.length) uploadMultiple(files);
              e.target.value = "";
            }} />
          <button className="gallery__upload-btn" onClick={() => fileRef.current.click()} disabled={uploading}>
            <span className="gallery__upload-icon"><UploadIcon /></span>
            {uploadProgress
              ? `Przesyłanie ${uploadProgress.current}/${uploadProgress.total}...`
              : "Dodaj zdjęcia / filmy"}
          </button>
        </div>

        {/* Controls row: columns + filter */}
        <div className="gallery__controls-row">
          <div className="gallery__col-row">
            {COL_OPTIONS.map((n) => (
              <button key={n} className={`gallery__col-btn ${cols === n ? "gallery__col-btn--active" : ""}`}
                onClick={() => changeCols(n)} title={`${n} kolumna`}>
                <ColIcon n={n} />
              </button>
            ))}
          </div>

          {/* Filter */}
          <div className="gallery__filter-wrap">
            <button
              className={`gallery__filter-btn ${filterNames.length ? "gallery__filter-btn--active" : ""}`}
              onClick={() => setShowFilter((v) => !v)}
            >
              👤 {filterNames.length ? `${filterNames.length} osób` : "Filtruj"}
            </button>
            {showFilter && (
              <div className="gallery__filter-panel">
                <div className="gallery__filter-header">
                  <span>Pokaż zdjęcia od:</span>
                  {filterNames.length > 0 && (
                    <button className="gallery__filter-clear" onClick={() => setFilterNames([])}>
                      Wyczyść
                    </button>
                  )}
                </div>
                <div className="gallery__filter-list">
                  {availableNames.map((name) => (
                    <label key={name} className="gallery__filter-item">
                      <input
                        type="checkbox"
                        checked={filterNames.includes(name)}
                        onChange={() => toggleFilter(name)}
                      />
                      <span>{name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Grid */}
        {displayPhotos.length === 0 && !loadingMore && !filterLoading ? (
          <div className="gallery__empty">
            <div className="gallery__empty-icon">📷</div>
            <p>{filterNames.length ? "Brak zdjęć dla wybranych osób." : "Bądź pierwszy i dodaj zdjęcie!"}</p>
          </div>
        ) : (
          <div className="gallery__grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {displayPhotos.map((photo) => (
              <div key={photo.id} className="gallery__photo-card" onClick={() => { setLightbox(photo); setCommentText(""); }}>
                {photo.mediaType === "video" ? (
                  <div className="gallery__video-thumb">
                    {photo.thumbUrl && (
                      <img
                        src={`${API}${photo.thumbUrl}`}
                        alt=""
                        className="gallery__photo-img"
                        loading="lazy"
                      />
                    )}
                    <div className="gallery__video-play"><PlayIcon /></div>
                  </div>
                ) : (
                  <img
                    src={`${API}${photo.thumbUrl || photo.url}`}
                    alt=""
                    className="gallery__photo-img"
                    loading="lazy"
                  />
                )}
                <div className="gallery__photo-overlay">
                  <span className="gallery__photo-name">{photo.uploaderName}</span>
                  {canDelete(photo) && (
                    <button className="gallery__delete-btn"
                      onClick={(e) => { e.stopPropagation(); requestDelete(photo); }}>
                      <TrashIcon />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel — hidden when filter active */}
        {filteredPhotos === null && (
          <div ref={sentinelRef} className="gallery__sentinel">
            {loadingMore && <div className="gallery__loading-more">Ładowanie...</div>}
            {!hasMore && photos.length > 0 && (
              <div className="gallery__end-note">Wszystkie zdjęcia załadowane ✓</div>
            )}
          </div>
        )}
        {filterLoading && (
          <div className="gallery__sentinel">
            <div className="gallery__loading-more">Ładowanie filtrowanych zdjęć...</div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <div className="lightbox__content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox__close" onClick={() => setLightbox(null)}><CloseIcon /></button>

            {lightbox.mediaType === "video" ? (
              <video
                src={`${API}${lightbox.url}`}
                controls
                preload="none"
                className="lightbox__video"
                playsInline
              />
            ) : (
              <ProgressiveImage
                thumb={lightbox.thumbUrl ? `${API}${lightbox.thumbUrl}` : null}
                full={`${API}${lightbox.url}`}
                alt=""
                className="lightbox__img"
              />
            )}

            <div className="lightbox__meta">
              <span className="lightbox__name">{lightbox.uploaderName}</span>
              {canDelete(lightbox) && (
                <button className="lightbox__delete-btn" onClick={() => requestDelete(lightbox)}>
                  <TrashIcon /> Usuń
                </button>
              )}
            </div>

            {/* Comments */}
            <div className="lightbox__comments">
              {(lightbox.comments || []).length > 0 && (
                <div className="lightbox__comments-list">
                  {(lightbox.comments || []).map((c) => (
                    <div key={c.id} className="lightbox__comment">
                      <span className="lightbox__comment-author">{c.authorName}</span>
                      <span className="lightbox__comment-text">{c.text}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="lightbox__comment-input-row">
                <input
                  className="lightbox__comment-input"
                  placeholder="Dodaj komentarz... (max 50 znaków)"
                  value={commentText}
                  maxLength={50}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitComment()}
                />
                <button
                  className="lightbox__comment-send"
                  onClick={submitComment}
                  disabled={!commentText.trim()}
                >
                  ➤
                </button>
              </div>
              {commentText.length > 0 && (
                <div className="lightbox__comment-count">{commentText.length}/50</div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmDialog icon="🗑️" title="Usunąć plik?" message={confirm.message}
          confirmLabel="Usuń" cancelLabel="Anuluj"
          onConfirm={executeDelete} onCancel={() => setConfirm(null)} />
      )}
      {deleteError && (
        <ConfirmDialog icon="⚠️" title="Błąd" message={deleteError}
          confirmLabel="OK" cancelLabel={null}
          onConfirm={() => setDeleteError(null)} onCancel={() => setDeleteError(null)} />
      )}
      {tutorial.visible && (
        <TutorialOverlay title="Galeria 📷" steps={TUTORIAL_STEPS} onClose={tutorial.dismiss} />
      )}
    </div>
  );
}
